import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Typography, Form, Input, Button, Space, Modal, Row, Col, message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { parseIds } from "../../utils/parseIds";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Página para visualizar múltiplos IDs do Datateam.
 *
 * Permite que o usuário visualize múltiplos demand IDs buscando em saj, projuris e peticiona.
 */
const DatateamVisualizarContent: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const demandIdsRef = useRef<HTMLTextAreaElement>(null);
  const [showBeautifyButton, setShowBeautifyButton] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  /**
   * Exemplos de IDs de demand para referência.
   */
  const exampleIds = [
    "203f8cfb-2dcc-4231-8ec7-b5c99d13ea8c",
    "fb0588a2-f808-44de-a2e6-e64cfcb31b8d",
    "eaea1d33-912e-4bad-8c2c-fc73a515a72d",
  ];

  /**
   * Valida os IDs e retorna mensagem de erro detalhada.
   *
   * OBS: A validação por formato (UUID regex) foi removida intencionalmente.
   * Aceitamos qualquer string não vazia como ID aqui — a distinção de "demanda"
   * será feita na página de monitoramento com base no campo `type` retornado
   * pela API.
   */
  const validateIds = (value: string): { valid: boolean; error?: string } => {
    if (!value || value.trim().length === 0) {
      return { valid: false, error: "Por favor, insira pelo menos um ID" };
    }

    const idList = parseIds(value);
    if (!Array.isArray(idList) || idList.length === 0) {
      return { valid: false, error: "Nenhum ID válido encontrado" };
    }

    // Aceita qualquer ID não vazio; não valida formato/UUID aqui.
    return { valid: true };
  };

  /**
   * Verifica se uma string é um JSON válido.
   */
  const isJsonValid = (str: string): boolean => {
    try {
      const parsed = JSON.parse(str);
      return Array.isArray(parsed);
    } catch (e) {
      return false;
    }
  };

  /**
   * Formata o JSON para melhor visualização.
   */
  const beautifyJson = () => {
    const value = form.getFieldValue("demandIds");
    if (isJsonValid(value)) {
      form.setFieldsValue({ demandIds: JSON.stringify(JSON.parse(value), null, 2) });
    }
  };

  /**
   * Handler para submeter o formulário.
   */
  const handleSubmit = async (values: { demandIds: string }) => {
    const demandIdsText = values.demandIds || "";
    if (demandIdsText.trim()) {
      const idList = parseIds(demandIdsText);

      if (idList.length > 0) {
        // Salvar IDs no localStorage para a página de monitoramento
        localStorage.setItem("datateamVisualizarIds", JSON.stringify(idList));
        navigate("/datateam/visualizar/monitoring");
      } else {
        message.error("Nenhum ID válido encontrado");
      }
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={4} style={{ marginBottom: 0 }}>
              <EyeOutlined /> Visualizar Vários - Datateam
            </Title>
            <Button type="primary" onClick={() => setShowExamplesModal(true)}>
              Ver exemplos
            </Button>
          </div>
          <Paragraph style={{ marginBottom: 8, fontSize: "13px" }}>
            Digite os IDs dos demands que deseja visualizar. Cada ID será buscado em saj, projuris e peticiona. Você pode usar dois formatos: um ID por linha ou um array JSON.
          </Paragraph>
        </div>

        <Modal
          title="Formatos Aceitos"
          open={showExamplesModal}
          onCancel={() => setShowExamplesModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowExamplesModal(false)}>
              Fechar
            </Button>,
          ]}
          width={1200}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 1 - Múltiplas linhas:</Text>
                <TextArea
                  value={exampleIds.join("\n")}
                  readOnly
                  rows={exampleIds.length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 2 - Array JSON:</Text>
                <TextArea
                  value={JSON.stringify(exampleIds, null, 2)}
                  readOnly
                  rows={JSON.stringify(exampleIds, null, 2).split("\n").length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
          </Row>
        </Modal>

        <Form form={form} onFinish={handleSubmit} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="demandIds"
            label="IDs dos Demands"
            validateStatus={validationError ? "error" : undefined}
            help={validationError}
            rules={[
              { required: true, message: "Por favor, digite pelo menos um ID!" },
              {
                validator: (_, value) => {
                  const validation = validateIds(value);
                  if (!validation.valid) {
                    return Promise.reject(new Error(validation.error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              ref={demandIdsRef}
              rows={10}
              placeholder='Digite os IDs (um por linha) ou um array JSON: ["id1", "id2", ...]'
              style={{ fontFamily: "monospace" }}
              onChange={(e) => {
                const value = e.target.value;
                setShowBeautifyButton(isJsonValid(value));

                if (value.trim().length === 0) {
                  setValidationError(undefined);
                  return;
                }

                const validation = validateIds(value);
                if (!validation.valid) {
                  setValidationError(validation.error);
                } else {
                  setValidationError(undefined);
                }
              }}
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                {showBeautifyButton && (
                  <Button onClick={beautifyJson}>JSON Beautify</Button>
                )}
              </div>
              <Button type="primary" htmlType="submit" size="large" icon={<EyeOutlined />}>
                Visualizar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

const DatateamVisualizar: React.FC = () => {
  return (
    <DatateamVisualizarContent />
  );
};

export default DatateamVisualizar;
